<template lang="pug">
.visualisations
  .chart-container
    h4 Non-renewable energy consumption (kWh/m²·yr)
    canvas#globalEPNRconsumptionChart(width="350" height="150")
  .chart-container
    h4 Emissions (tCO₂/yr)
    canvas#ghgChart(width="350" height="150")
  .chart-container
    h4 Estimated annual energy bill (€/yr)
    canvas#energyBillChart(width="350" height="150")
</template>

<script lang="ts" setup>
import { onMounted, onUnmounted } from "vue";
import {
  Chart,
  LineController,
  LineElement,
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import type { IStagesChartData } from "@/types";

const props = defineProps<{
  stagesChartData: IStagesChartData[],
  endYear: number,
}>();

Chart.register(
  LineController,
  LineElement,
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Filler,
  Tooltip,
  Legend
);

onMounted(() => {
  const rows = [...props.stagesChartData].sort((a,b) => a.year - b.year);

  const labels = rows.map((r, i) => `${r.year}-${rows[i + 1]?.year ?? props.endYear}`);
  const epnr  = rows.map(r => r.globalEPNRconsumption);
  const co2   = rows.map(r => r.globalCO2emissions);
  const bill  = rows.map(r => r.energyBillGlobal);
  new Chart(
      document.getElementById("globalEPNRconsumptionChart") as HTMLCanvasElement,
      {
        type: "line",
        data: {
          labels,
          datasets: [{
            label: "Non-renewable energy",
            data: epnr,
            fill: true,
            tension: 0.25,
            pointRadius: 3,
          }]
        },
        options: {
          responsive: true,
          plugins: {
            legend: { display: true },
            datalabels: { display: false },
            tooltip: {
              callbacks: { label: ctx => `${ctx.formattedValue} kWh/m²·yr` }
            }
          },
          scales: {
            y: { title: { display: true, text: "kWh/m²·yr" } }
          }
        }
      }
  );

  new Chart(
      document.getElementById("ghgChart") as HTMLCanvasElement,
      {
        type: "line",
        data: {
          labels,
          datasets: [{
            label: "GHG emissions",
            data: co2,
            fill: false,
            tension: 0.25,
            pointRadius: 3,
          }]
        },
        options: {
          responsive: true,
          plugins: {
            legend: { display: true },
            datalabels: { display: false },
            tooltip: {
              callbacks: { label: ctx => `${ctx.formattedValue} tCO₂/yr` }
            }
          },
          scales: {
            y: { title: { display: true, text: "tCO₂/yr" } }
          }
        }
      }
  );

  new Chart(
      document.getElementById("energyBillChart") as HTMLCanvasElement,
      {
        type: "bar",
        data: {
          labels,
          datasets: [{
            label: "Energy bill",
            data: bill,
            borderWidth: 1
          }]
        },
        options: {
          responsive: true,
          plugins: {
            legend: { display: true },
            datalabels: { display: false },
            tooltip: {
              callbacks: { label: ctx => `€${ctx.formattedValue}/yr` }
            }
          },
          scales: {
            y: { title: { display: true, text: "€ / yr" } }
          }
        }
      }
  );
});

</script>

<style scoped lang="scss">
.visualisations {
  display: grid;
  gap: 2rem;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  align-items: center;
}
.chart-container {
  background: $background-color-default;
  border-radius: 0.5rem;
  padding: 1rem;
  canvas {
    width: 100%;
    height: auto;
  }
}
h4 {
  margin-top: 0;
  margin-bottom: 1.5rem;
  text-align: center;
  font-weight: normal;
}
</style>
